import React, { useContext } from "react";
import { LocaleContext } from "../../contexts/LocaleContext";
import { graphql, useStaticQuery } from "gatsby";
import { Logo } from "../Assets";
import { SocialMedia } from "../SocialMedia";
import styled from "styled-components";
import { theme } from "../../theme/theme";
import { HubSpotFormNewsletter } from "../Forms/HubSpotFormNewsletter";
import { ContentContainer } from "./ContentContainer";
import { mediaQuery } from "../../theme/constants";
import { Link } from "../Link";
import { useLocale } from "../../hooks/useLocale";
import { findByLocale } from "./findByLocale";

interface FooterProps {
  compact?: boolean;
}

const Credits = () => (
  <div className="credits">
    <p>© {new Date().getFullYear()} LaaS Company</p>
  </div>
);

const Query = graphql`
  query FooterQuery($locale: String) {
    story: datoCmsStory(locale: $locale) {
      slug
      title
    }
    references: datoCmsReferencesPage(locale: $locale) {
      slug
      title
      _allTitleLocales {
        locale
        value
      }
    }
    blog: datoCmsBlog(locale: $locale) {
      slug
      title
      _allTitleLocales {
        locale
        value
      }
    }
    jobs: datoCmsJobsPage(locale: $locale) {
      slug
      title
      _allTitleLocales {
        locale
        value
      }
    }
    contact: datoCmsContact(locale: $locale) {
      slug
      title
      _allTitleLocales {
        locale
        value
      }
      socialMediaAccounts {
        socialMedia
        url
      }
    }

    gdpr: datoCmsGdpr(locale: $locale) {
      slug
      title
      _allTitleLocales {
        locale
        value
      }
    }
    pricing: datoCmsPricing(locale: $locale) {
      title
      slug
      _allTitleLocales {
        locale
        value
      }
    }
    legals: datoCmsLegal(locale: $locale) {
      title
      slug
      pageTitle
      _allPageTitleLocales {
        locale
        value
      }
    }
    allDatoCmsLegal {
      edges {
        node {
          slug
          baseSlug
        }
      }
    }
    global: datoCmsGlobal(locale: $locale) {
      _allNewsletterFormTitleLocales {
        value
        locale
      }
    }
  }
`;

export const Footer = ({ compact = false }: FooterProps) => {
  const data = useStaticQuery(Query);
  const locale = useLocale();

  const firstLegalSlug = data.allDatoCmsLegal.edges[0].node.slug;
  const firstLegalBaseSlug = data.allDatoCmsLegal.edges[0].node.baseSlug;

  //TODO: Add pricing after references
  const mainMenu = [data.references, data.blog, data.contact]
    //hide blog  from english site
    .filter((_, i) => locale === "fi" || i !== 1);

  const gdprSlug = data.gdpr.slug;
  const gdprTitle = findByLocale(data.gdpr._allTitleLocales, locale);

  const jobsSlug = data.jobs.slug;
  const jobsTitle = findByLocale(data.jobs._allTitleLocales, locale);

  if (compact) {
    return (
      <FooterStyled compact={compact}>
        <Credits />
      </FooterStyled>
    );
  }

  return (
    <FooterStyled compact={!!compact}>
      <Container>
        <div className="flex flex-1 col">
          <Logo className={undefined} />
        </div>
        <nav className="flex flex-2 col">
          {mainMenu.map((i) => (
            <Link key={i.slug} to={`/${i.slug}`}>
              {findByLocale(i._allTitleLocales, locale)}
            </Link>
          ))}
          <div className="Divider" />
          <Link to={`/${gdprSlug}`} target="_blank" rel="noreferrer">
            {gdprTitle}
          </Link>
          <Link to={`/${firstLegalBaseSlug}/${firstLegalSlug}`}>
            {findByLocale(data.legals._allPageTitleLocales, locale)}
          </Link>
          {locale === "fi" && <Link to={`/${jobsSlug}`}>{jobsTitle}</Link>}
        </nav>
        <div className="Newsletter flex flex-3 col">
          <div>
            <p className="cta">
              {
                data.global._allNewsletterFormTitleLocales.find(
                  (v: any) => v.locale === locale
                )?.value
              }
            </p>
          </div>
          <HubSpotFormNewsletter />
          <SocialMedia data={data.contact.socialMediaAccounts} />
        </div>
      </Container>
      <Credits />
    </FooterStyled>
  );
};

const Container = styled(ContentContainer)`
  flex-direction: row;
  padding-bottom: 20px;
`;

const FooterStyled = styled.footer<{ compact: boolean }>`
  position: relative;
  display: flex;
  padding-top: ${(p) => (p.compact ? 0 : 80)}px;
  padding-bottom: 0;
  color: ${theme.primary};
  flex-direction: column;
  .SocialMedia {
    margin-top: 20px;
  }
  .credits {
    margin-top: ${(p) => (p.compact ? 18 : 40)}px;
    p {
      font-size: 15px;
      text-align: center;
    }
  }
  .Newsletter {
    p.cta {
      font-weight: 700;
    }
    input::placeholder {
      color: ${theme.primary};
      opacity: 0.4;
    }
    ${mediaQuery.medium} {
      margin-top: 50px;
    }
  }

  > div {
    ${mediaQuery.medium} {
      flex-direction: column;
    }
  }
  > div > .col:first-child {
    max-width: 220px;
  }
  .Logo {
    margin-right: 120px;
    width: 120px;
    height: 40px;
    ${mediaQuery.medium} {
      width: 90px;

      margin-bottom: 40px;
    }
  }
  nav {
    h4 {
      margin-bottom: 10px;
    }
    a {
      padding: 8px 0;
      transition: all 0.2s;
      &:hover {
        color: ${theme.accent};
      }
    }
    .Divider {
      height: 20px;
    }
  }
`;
